import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Quote, Users, Split } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { allCategories, catLabel } from "@/lib/lore";
import { useEntities, useInvalidate } from "@/lib/queries";
import { SpeakerSelect } from "@/components/quotes/SpeakerSelect";
import { QuoteLinksFields } from "@/components/quotes/QuoteLinksFields";

const initChoice = (s, entities) => {
  if (s.match) return { action: "link", entity_id: s.match.id, category: "Character" };
  const c = s.candidates?.[0];
  if (c && c.score >= 85) return { action: "link", entity_id: c.id, category: "Character" };
  const exact = entities.find((e) => e.name.toLowerCase() === s.name.toLowerCase());
  return exact ? { action: "link", entity_id: exact.id, category: "Character" } : { action: "stub", entity_id: null, category: "Character" };
};

const SpeakerRow = ({ name, choice, entities, onChange, cand }) => {
  const value = choice.action === "link" ? `link:${choice.entity_id}` : choice.action;
  const set = (v) => onChange(v.startsWith("link:") ? { ...choice, action: "link", entity_id: v.slice(5) } : { ...choice, action: v, entity_id: null });
  return (
    <div className="space-y-2 rounded-md border border-[#2D3442] bg-[#0F131A] p-3" data-testid={`quote-speaker-${name}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-display text-xl text-[#F3EBDD]">{name}</span>
        {choice.action === "link" ? <span className="text-xs text-[#10B981]">linked</span> : choice.action === "none" ? <span className="text-xs text-[#F59E0B]">unattributed</span> : <span className="text-xs text-[#A8A090]">no entry yet</span>}
      </div>
      {cand && choice.action !== "link" && <p className="text-xs text-[#F59E0B]">Maybe the same as {cand.name}? Pick it below to link.</p>}
      <div className="flex flex-wrap gap-2">
        <SpeakerSelect entities={entities} value={value} onChange={set} testid={`quote-speaker-choice-${name}`} className="h-9 flex-1"
          extra={[{ value: "stub", label: "+ Create a stub entry" }, { value: "entry", label: "+ Create a full entry" }, { value: "none", label: "Leave unattributed" }]} />
        {(choice.action === "stub" || choice.action === "entry") && (
          <Select value={choice.category} onValueChange={(c) => onChange({ ...choice, category: c })}>
            <SelectTrigger className="h-9 w-36" data-testid={`quote-speaker-category-${name}`}><SelectValue /></SelectTrigger>
            <SelectContent>{allCategories().map((c) => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
};

const GroupEditor = ({ g, gi, onChange, uid }) => (
  <div className="space-y-2 rounded-md border border-[#2D3442] p-3" data-testid={`quote-group-${gi}`}>
    <div className="lf-label">{g.lines.length > 1 ? "Conversation" : "Quote"} {gi + 1}</div>
    {g.lines.map((ln, li) => (
      <div key={li} className="grid gap-1.5 sm:grid-cols-[140px_1fr]">
        <Input value={ln.speaker} onChange={(e) => onChange({ ...g, lines: g.lines.map((x, j) => (j === li ? { ...x, speaker: e.target.value } : x)) })}
          placeholder="Unattributed" className="h-9 text-sm" data-testid={`quote-line-speaker-${gi}-${li}`} />
        <Textarea rows={2} value={ln.text} onChange={(e) => onChange({ ...g, lines: g.lines.map((x, j) => (j === li ? { ...x, text: e.target.value } : x)) })}
          className="font-display text-lg" data-testid={`quote-line-text-${gi}-${li}`} />
      </div>
    ))}
    <Input value={g.context} onChange={(e) => onChange({ ...g, context: e.target.value })} placeholder="Context (optional)" className="h-9 text-sm" data-testid={`quote-group-context-${gi}`} />
    <details className="group/links">
      <summary className="cursor-pointer text-xs text-[#F59E0B] hover:underline" data-testid={`quote-group-links-toggle-${gi}`}>
        Tags & events{(g.tags?.length || g.event_ids?.length) ? ` · ${g.tags?.length || 0} tags · ${g.event_ids?.length || 0} events` : " (optional)"}
      </summary>
      <div className="pt-2">
        <QuoteLinksFields uid={uid} tags={g.tags || []} eventIds={g.event_ids || []} onTags={(t) => onChange({ ...g, tags: t })} onEvents={(e) => onChange({ ...g, event_ids: e })} testid={`quote-group-links-${gi}`} />
      </div>
    </details>
  </div>
);

export const QuotePreview = ({ data, uid, onDone, onCancel }) => {
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const { data: entities = [] } = useEntities(uid);
  const [groups, setGroups] = useState(data.groups);
  const [combine, setCombine] = useState(data.groups.length > 1 ? null : false);
  const [choices, setChoices] = useState(() => Object.fromEntries(data.speakers.map((s) => [s.name, initChoice(s, entities)])));
  const [busy, setBusy] = useState(false);
  const names = useMemo(() => [...new Set(groups.flatMap((g) => g.lines.map((l) => l.speaker.trim())).filter(Boolean))], [groups]);
  const choiceOf = (n) => choices[n] || initChoice({ name: n }, entities);
  const unattributed = groups.some((g) => g.lines.some((l) => !l.speaker.trim())) || names.some((n) => choiceOf(n).action === "none");

  const save = async () => {
    setBusy(true);
    try {
      const speakers = Object.fromEntries(names.map((n) => [n, choiceOf(n)]));
      const { data: res } = await api.post(`/universes/${uid}/quotes/commit`, { groups: groups.map((g) => ({ lines: g.lines, context: g.context, tags: g.tags || [], event_ids: g.event_ids || [] })), combine: !!combine, speakers });
      invalidate(["quotes", uid], "entity-quotes", ["entities", uid]);
      const full = res.created.find((c) => !c.is_stub);
      toast.success(`Saved ${res.quote_ids.length} quote${res.quote_ids.length === 1 ? "" : "s"} to the library`, {
        description: res.created.length ? `New entries: ${res.created.map((c) => c.name).join(", ")}` : undefined,
        action: full ? { label: `Fill in ${full.name}`, onClick: () => navigate(`/u/${uid}/lore/${full.id}`) } : { label: "Open library", onClick: () => navigate(`/u/${uid}/quotes`) },
      });
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5 rounded-md border border-[#D97706]/30 bg-[#13171F] p-6" data-testid="quote-preview">
      <div className="flex items-center gap-2 text-[#F59E0B]"><Quote className="h-4 w-4" /><span className="lf-label text-[#F59E0B]">Quote · review before saving</span></div>
      {data.ai_failed && <p className="text-sm text-[#E8DFD1]" data-testid="quote-ai-failed">The scribe couldn't split this note automatically. Check the text and speaker below.</p>}
      {groups.map((g, gi) => <GroupEditor key={gi} g={g} gi={gi} uid={uid} onChange={(ng) => setGroups(groups.map((x, j) => (j === gi ? ng : x)))} />)}
      {groups.length > 1 && (
        <div className="rounded-md border border-[#F59E0B]/40 bg-[#F59E0B]/5 p-3" data-testid="quote-combine-choice">
          <p className="mb-2 text-sm text-[#E8DFD1]">This note has {groups.length} separate quotes. How should they be saved?</p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant={combine === false ? "default" : "outline"} onClick={() => setCombine(false)} data-testid="quote-combine-separate"><Split className="mr-1 h-3.5 w-3.5" />Separate quotes</Button>
            <Button size="sm" variant={combine === true ? "default" : "outline"} onClick={() => setCombine(true)} data-testid="quote-combine-together"><Users className="mr-1 h-3.5 w-3.5" />One conversation</Button>
          </div>
        </div>
      )}
      <div>
        <div className="lf-label mb-2">Speakers</div>
        {names.length === 0 && <p className="text-sm text-[#A8A090]">No speaker given.</p>}
        <div className="space-y-2">
          {names.map((n) => (
            <SpeakerRow key={n} name={n} choice={choiceOf(n)} entities={entities} cand={data.speakers.find((s) => s.name === n)?.candidates?.[0]}
              onChange={(c) => setChoices({ ...choices, [n]: c })} />
          ))}
        </div>
        {unattributed && <p className="mt-2 text-xs text-[#F59E0B]" data-testid="quote-unattributed-note">Lines without a linked speaker are saved as Unattributed. You can assign them later in the Quote Library.</p>}
      </div>
      <div className="sticky bottom-0 -mx-4 flex justify-end gap-2 border-t border-[#2D3442] bg-[#0F131A] px-4 py-3 sm:static sm:mx-0 sm:border-0 sm:bg-transparent sm:px-0 sm:py-0">
        <Button variant="ghost" onClick={onCancel} data-testid="quote-cancel-button">Discard</Button>
        <Button onClick={save} disabled={busy || combine === null} data-testid="quote-save-button">{busy ? "Saving…" : combine === null ? "Choose how to save" : "Save to Quote Library"}</Button>
      </div>
    </div>
  );
};
